import {
  type CaraWorkspacePreset,
  CustomPresetState,
  type GeneratePresetQuestionsInput,
  PresetGenerationStep,
  type PresetGenerationQaAnswerInput,
  type StartPresetGenerationInput
} from '~/api/graphql';
import { type CaraAttachment, idsByKind } from '~/components/cara-input/cara-attachment';

/** State and pure transitions for the "Convert to Preset" wizard (no React). */

/**
 * Must equal the backend's `cara.MaxPresetGenerationSourceWorkspaces` (= 3):
 * `startPresetGeneration` / `generatePresetQuestions` reject larger selections,
 * so bumping this alone just moves the failure to the wizard's last step.
 */
export const MAX_CONVERT_TO_PRESET_WORKSPACES = 3;

export type ConvertToPresetStep = 1 | 2 | 3 | 4 | 5;

export const CONVERT_TO_PRESET_STEPS: readonly ConvertToPresetStep[] = [1, 2, 3, 4, 5];

export const GENERATION_STEP: ConvertToPresetStep = 5;

export const CONVERT_TO_PRESET_STEP_LABELS: Record<ConvertToPresetStep, string> = {
  1: 'Overview',
  2: 'Workspaces',
  3: 'Defaults',
  4: 'Questions',
  5: 'Generate'
};

export const PRESET_TASK_DEFINITION_QUESTION =
  'What is the task this preset should codify? Describe what you want Cara to do every time.';

/**
 * Mirrors the PresetClarifyingQuestionFields fragment but is declared locally so
 * the reducer and its tests do not depend on GraphQL codegen (same rationale as
 * presets.utils' PresetTemplateRefSource).
 */
export type WizardClarifyingQuestion = {
  id: string;
  question: string;
  options: string[];
  allowFreeText: boolean;
};

/**
 * A file the picker can offer. `url` is absent because it is derived from the id
 * (buildAgencyDocumentStaticsUrl); `mimeType` is absent for a searched file, whose
 * kind is read off the name instead.
 */
export type PresetFileOption = {
  id: string;
  name: string;
  mimeType?: string | null;
};

export type ConvertToPresetWizardState = {
  step: ConvertToPresetStep;
  sourceWorkspaceId: string;
  workspaceIds: string[];
  basePreset: CaraWorkspacePreset | null;
  /**
   * Files reached through search. The source workspace's own files are derived from
   * the workspace, so only these need storing; both render in one grid.
   */
  addedFiles: PresetFileOption[];
  /** The attachment set, whatever a file's origin. Sent as fileIds verbatim. */
  selectedFileIds: string[];
  outputTemplates: CaraAttachment[];
  expectedOutputFormats: string;
  taskDefinition: string;
  aiQuestions: WizardClarifyingQuestion[] | null;
  questionAnswers: Record<string, string>;
};

export const initialConvertToPresetWizardState = (args: {
  sourceWorkspaceId: string;
  basePreset: CaraWorkspacePreset | null;
}): ConvertToPresetWizardState => ({
  step: 1,
  sourceWorkspaceId: args.sourceWorkspaceId,
  workspaceIds: [args.sourceWorkspaceId],
  basePreset: args.basePreset,
  addedFiles: [],
  selectedFileIds: [],
  outputTemplates: [],
  expectedOutputFormats: '',
  taskDefinition: '',
  aiQuestions: null,
  questionAnswers: {}
});

/**
 * Applied on every selection change, so no path can drop the source workspace or
 * exceed the cap (the picker also disables options at the cap).
 */
export const clampWorkspaceSelection = (ids: string[], sourceWorkspaceId: string): string[] =>
  Array.from(new Set([sourceWorkspaceId, ...ids])).slice(0, MAX_CONVERT_TO_PRESET_WORKSPACES);

export const isAtWorkspaceCapacity = (ids: string[]): boolean =>
  ids.length >= MAX_CONVERT_TO_PRESET_WORKSPACES;

/**
 * A deprecated built-in preset the dropdown no longer offers falls back to null,
 * so the wizard never defaults to an option it cannot show.
 */
export const defaultBasePreset = (
  preset: CaraWorkspacePreset | null | undefined,
  validPresets: CaraWorkspacePreset[]
): CaraWorkspacePreset | null => (preset && validPresets.includes(preset) ? preset : null);

export const nextConvertToPresetStep = (step: ConvertToPresetStep): ConvertToPresetStep =>
  Math.min(step + 1, CONVERT_TO_PRESET_STEPS.length) as ConvertToPresetStep;

export const previousConvertToPresetStep = (step: ConvertToPresetStep): ConvertToPresetStep =>
  Math.max(step - 1, 1) as ConvertToPresetStep;

export const canProceedFromStep = (state: ConvertToPresetWizardState): boolean => {
  switch (state.step) {
    case 2:
      return state.workspaceIds.length >= 1;
    case 4:
      return state.taskDefinition.trim().length > 0;
    case 1:
    case 3:
    case 5:
      return true;
  }
};

export type ConvertToPresetWizardAction =
  | { type: 'reset'; state: ConvertToPresetWizardState }
  | { type: 'next' }
  | { type: 'back' }
  | { type: 'setWorkspaceIds'; workspaceIds: string[] }
  | { type: 'setBasePreset'; basePreset: CaraWorkspacePreset | null }
  | { type: 'addFile'; file: PresetFileOption }
  | { type: 'toggleFile'; fileId: string }
  | { type: 'addOutputTemplates'; templates: CaraAttachment[] }
  | { type: 'removeOutputTemplate'; id: string }
  | { type: 'setExpectedOutputFormats'; value: string }
  | { type: 'setTaskDefinition'; value: string }
  | { type: 'setAiQuestions'; questions: WizardClarifyingQuestion[] }
  | { type: 'setQuestionAnswer'; questionId: string; answer: string };

/**
 * Applied whenever a question-shaping input changes, so generation never
 * receives answers to questions asked about a selection that no longer exists.
 * The free-text fields are exempt: clearing per keystroke would destroy answers
 * mid-typing.
 */
const withAiQuestionsCleared = (state: ConvertToPresetWizardState): ConvertToPresetWizardState => ({
  ...state,
  aiQuestions: null,
  questionAnswers: {}
});

export const convertToPresetWizardReducer = (
  state: ConvertToPresetWizardState,
  action: ConvertToPresetWizardAction
): ConvertToPresetWizardState => {
  switch (action.type) {
    case 'reset':
      return action.state;
    case 'next':
      return { ...state, step: nextConvertToPresetStep(state.step) };
    case 'back':
      return { ...state, step: previousConvertToPresetStep(state.step) };
    case 'setWorkspaceIds': {
      const workspaceIds = clampWorkspaceSelection(action.workspaceIds, state.sourceWorkspaceId);
      // The clamp can map a picker event back to the current selection, which
      // must not invalidate the cached questions.
      const unchanged =
        workspaceIds.length === state.workspaceIds.length &&
        workspaceIds.every((id, i) => id === state.workspaceIds[i]);
      if (unchanged) return state;
      return withAiQuestionsCleared({ ...state, workspaceIds });
    }
    case 'setBasePreset':
      if (action.basePreset === state.basePreset) return state;
      return withAiQuestionsCleared({ ...state, basePreset: action.basePreset });
    // Files do not shape the clarifying questions (they are absent from
    // assembleGeneratePresetQuestionsInput), so neither file action clears their cache.
    case 'addFile': {
      // Searching a file out is a deliberate act, so it arrives attached. Adding one
      // already offered by the grid only ticks it.
      const known = state.addedFiles.some((f) => f.id === action.file.id);
      return {
        ...state,
        addedFiles: known ? state.addedFiles : [...state.addedFiles, action.file],
        selectedFileIds: state.selectedFileIds.includes(action.file.id)
          ? state.selectedFileIds
          : [...state.selectedFileIds, action.file.id]
      };
    }
    case 'toggleFile':
      return {
        ...state,
        selectedFileIds: state.selectedFileIds.includes(action.fileId)
          ? state.selectedFileIds.filter((id) => id !== action.fileId)
          : [...state.selectedFileIds, action.fileId]
      };
    case 'addOutputTemplates': {
      // The template pickers dispatch remove-then-add on a selection change, and
      // React applies queued reducer actions in order, so deduping by id here
      // stays correct.
      const existing = new Set(state.outputTemplates.map((a) => a.id));
      const additions = action.templates.filter((a) => !existing.has(a.id));
      if (additions.length === 0) return state;
      return withAiQuestionsCleared({
        ...state,
        outputTemplates: [...state.outputTemplates, ...additions]
      });
    }
    case 'removeOutputTemplate': {
      const outputTemplates = state.outputTemplates.filter((a) => a.id !== action.id);
      if (outputTemplates.length === state.outputTemplates.length) return state;
      return withAiQuestionsCleared({ ...state, outputTemplates });
    }
    case 'setExpectedOutputFormats':
      return { ...state, expectedOutputFormats: action.value };
    case 'setTaskDefinition':
      return { ...state, taskDefinition: action.value };
    case 'setAiQuestions':
      return { ...state, aiQuestions: action.questions };
    case 'setQuestionAnswer':
      return {
        ...state,
        questionAnswers: { ...state.questionAnswers, [action.questionId]: action.answer }
      };
  }
};

/**
 * Order is hand-chosen — the generated enum is alphabetized, and the UI marks
 * earlier steps done and later ones pending relative to the current step.
 */
export const PRESET_GENERATION_PROGRESS_STEPS: readonly PresetGenerationStep[] = [
  PresetGenerationStep.ReviewingWorkspaces,
  PresetGenerationStep.DetectingTemplates,
  PresetGenerationStep.DraftingPrompt
];

export const PRESET_GENERATION_PROGRESS_LABELS: Record<PresetGenerationStep, string> = {
  [PresetGenerationStep.ReviewingWorkspaces]: 'Reviewing workspaces',
  [PresetGenerationStep.DetectingTemplates]: 'Detecting templates',
  [PresetGenerationStep.DraftingPrompt]: 'Drafting the preset prompt'
};

export type PresetGenerationStatus = 'idle' | 'starting' | 'running' | 'completed' | 'failed';

/**
 * A failed start (network / gate error, before the preset row exists) is surfaced
 * as `failed` so the retry path is reachable.
 */
export const derivePresetGenerationStatus = (args: {
  starting: boolean;
  startFailed: boolean;
  presetState: CustomPresetState | null | undefined;
}): PresetGenerationStatus => {
  if (args.startFailed) return 'failed';
  switch (args.presetState) {
    case CustomPresetState.Failed:
      return 'failed';
    case CustomPresetState.Ready:
    case CustomPresetState.NeedsReview:
      return 'completed';
    case CustomPresetState.Generating:
      return 'running';
    default:
      return args.starting ? 'starting' : 'idle';
  }
};

export const isPresetGenerationInFlight = (status: PresetGenerationStatus): boolean =>
  status === 'starting' || status === 'running';

/** The required task definition is always the first answer. */
export const presetGenerationQaAnswers = (
  state: ConvertToPresetWizardState
): PresetGenerationQaAnswerInput[] => {
  const answers: PresetGenerationQaAnswerInput[] = [];

  const taskDefinition = state.taskDefinition.trim();
  if (taskDefinition.length > 0) {
    answers.push({ question: PRESET_TASK_DEFINITION_QUESTION, answer: taskDefinition });
  }

  // An unanswered question is simply not sent — there is no separate skip.
  for (const question of state.aiQuestions ?? []) {
    const answer = (state.questionAnswers[question.id] ?? '').trim();
    if (answer.length === 0) continue;
    answers.push({ question: question.question, answer });
  }

  return answers;
};

/**
 * The subset of wizard state that shapes the clarifying questions (no files, no
 * answers), so the generated questions do not re-ask what the user provided.
 */
export const assembleGeneratePresetQuestionsInput = (
  state: ConvertToPresetWizardState
): GeneratePresetQuestionsInput => {
  const wordDocumentTemplateIds = idsByKind(state.outputTemplates, 'word-template');
  const htmlDocumentTemplateIds = idsByKind(state.outputTemplates, 'html-template');
  const customFormIds = idsByKind(state.outputTemplates, 'custom-form');
  const outputFormatsText = state.expectedOutputFormats.trim();

  return {
    sourceWorkspaceIds: state.workspaceIds,
    basePresetOverride: state.basePreset ?? undefined,
    wordDocumentTemplateIds:
      wordDocumentTemplateIds.length > 0 ? wordDocumentTemplateIds : undefined,
    htmlDocumentTemplateIds:
      htmlDocumentTemplateIds.length > 0 ? htmlDocumentTemplateIds : undefined,
    customFormIds: customFormIds.length > 0 ? customFormIds : undefined,
    outputFormatsText: outputFormatsText.length > 0 ? outputFormatsText : undefined
  };
};

/**
 * Pure, so the retry path re-assembles an identical input from the same state.
 * Empty optional lists / strings are sent as `undefined` (unset), never `[]`/`''`.
 */
export const assembleStartPresetGenerationInput = (
  state: ConvertToPresetWizardState
): StartPresetGenerationInput => {
  const fileIds = state.selectedFileIds;
  const qaAnswers = presetGenerationQaAnswers(state);

  return {
    ...assembleGeneratePresetQuestionsInput(state),
    fileIds: fileIds.length > 0 ? fileIds : undefined,
    qaAnswers: qaAnswers.length > 0 ? qaAnswers : undefined
  };
};
